import type {ErrorMessageToken, ErrorParsed} from "~/types/errors";
import Bugsnag from "@bugsnag/js";

export function useWadError() {
  const getErrorMessageTokens = (errorMessage: string | null): ErrorMessageToken[] | null => {
    if(!errorMessage) {
      return null;
    }
    const tokens: ErrorMessageToken[] = []
    const links = errorMessage.match(/<<.+?>>/g)

    if (links && links.length) {
      let message = errorMessage
      for (let i = 0; i < links.length; i++) {
        const currentLink = links[i];
        if (currentLink) {
          const linkPosition = message.indexOf(currentLink)
          if (linkPosition > 0) {
            tokens.push({ text: message.substring(0, linkPosition), isLink: false })
          }
          tokens.push({ text: currentLink.replace(/<<|>>/mg, ''), isLink: true })
          message = message.substring(linkPosition + currentLink.length)
        }
      }
      if (message) {
        tokens.push({ text: message, isLink: false })
      }
    } else {
      tokens.push({ text: errorMessage, isLink: false })
    }

    return tokens
  }

  const wadGenericError = 'Sorry, something just went wrong. Please try again in a few moments. If you continue to get this error please contact support.';

  /**
   * The `error_code` values whose `message` may be shown as-is.
   *
   * Not all of them can be. `ErrorNotFound` alone carries 77 distinct messages, largely internal
   * ("Item not found", "CandidateResource not found"), and the default ACCESS_FORBIDDEN covers
   * integration-token and admin failures. Those stay generic.
   *
   * The line is drawn to match what users could already see, plus what was being lost. Only the
   * resources setting `$wrap = null` used to reach a screen, because the message was read one level
   * too shallow - so BAD_DATA and TOO_MANY_ATTEMPTS were always displayed and stay displayed here.
   * Dropping BAD_DATA replaces "The e-mail or password is incorrect" with the generic string on the
   * most common failure on the login screen, which is a regression, not a tightening.
   *
   * Matched on the code and never on the message text, which would break the first time anyone
   * edits copy. Mirrors ErrorCodeEnum::isDisplayableMessage() in wad-api - keep the two in step.
   */
  const displayableErrorCodes = [
    // Were always visible (flat resources) - withholding them now would be a regression
    'BAD_DATA',
    'TOO_MANY_ATTEMPTS',
    'MFA_TOKEN_EXPIRED',
    'MFA_NOT_ACTIVE',
    // Were nested, so never reached a screen, and carry copy written to tell somebody where to go
    'ACCOUNT_SUSPENDED',
    'ACCOUNT_MOVED',
    'SIGNUP_MOVED',
  ];

  const parseErrorFromException = (exception: any, handleValidation: boolean = true, customMessage: string|null = null) => {
    // The API wraps most responses one level down - { data: { success, error_code, message } } -
    // and ofetch hands that whole body over as exception.data, so it sits at exception.data.data.
    // Only the four resources that set $wrap = null are flat. Reading one level too shallow found
    // undefined on every nested response, which is why 403, 404 and the MFA errors never reached a
    // screen while 412 and 429 did.
    const payload = exception.data?.data ?? exception.data ?? {};

    // Show the server's own sentence only when it was written to be read. Everything else gets the
    // generic string, so an internal message cannot reach a user by accident.
    const errorCode = payload.error_code || null;
    const msg = displayableErrorCodes.includes(errorCode) ? (payload.message || null) : null;

    const errorData = <ErrorParsed>{
      fields: {},
      message: customMessage || (msg || wadGenericError),
      type: 'error',
      status: exception.status || null,
      errorCode
    }
    if(errorData.status === 422 && handleValidation) {
      let responseData = exception.data || {};
      if(responseData.data) {
        responseData = responseData.data
      }
      const errors = responseData.errors ? responseData.errors : responseData;
      for (const [key, value] of Object.entries(errors)) {
        errorData.fields[key] = Array.isArray(value) ? value[0] : value;
      }
      if(Object.keys(errorData.fields).length){
        errorData.message = null;
      }
    } else if (exception.name === 'FetchError' && !navigator.onLine) {
      errorData.message = 'It seems that you are offline. Please check your internet connection and try again.';
    }

    // The 550 branch that used to sit here did its own unwrap and re-assigned the message the
    // top-level read now produces. ErrorGeneralException's message is not written for users, so it
    // is deliberately not allowlisted and renders generic - which is what it always looked like in
    // practice, since its copy is "Something went wrong, please try again later".

    // A 4xx is the server deliberately answering the client, and it arrives carrying a sentence
    // written for the person reading it: a wrong password, an expired code, an account that no
    // longer belongs here. None of those are exceptions, and reporting them meant every failed
    // login opened a Bugsnag event. 5xx, the custom 550 and network failures still report, so
    // nothing that reached Bugsnag before is silently dropped.
    const status = errorData.status;
    const isClientError = typeof status === 'number' && status >= 400 && status < 500;
    if(!isClientError) {
      Bugsnag.notify(exception);
    }
    return errorData;
  }

  return {
    wadGenericError,
    getErrorMessageTokens,
    parseErrorFromException
  }
}
